import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import {
    trackAnalyticsEvent,
    trackEventFromState,
} from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import React, {
    FocusEventHandler,
    MouseEventHandler,
    useContext,
    useRef,
} from 'react';
import { connect } from 'react-redux';
import { useMount } from 'react-use';
import { SearchEventCardStyleSwitcher } from '../../../../components/EventCard';
import { SEARCH_SECTION } from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { getEventExpansions as getEventExpansionsAction } from '../../../../redux/actions/expansions';
import { setHoverEventTarget as setHoverEventTargetAction } from '../../../../redux/actions/ui';
import { TrackingContext } from '../../../../trackingContext';
import {
    Event,
    Events,
    FeatureFlags,
    SearchEventCardProps,
} from '../../../../types/index';
import { getSearchContent, getSearchSection } from '../../../../utils/general';
import { addSearchIdToGaAction } from '../../utils/analytics';
import { getEventResults, hasEventResults } from '../../utils/events';

const LoadingCards = loadable(
    () => import('../../../../components/LoadingCards'),
);

const LIST_TYPE = 'list';
const SEARCH_PLACEMENT_NAME = 'search';

function aggregateContext({
    id,
    action,
    events = [],
    searchId,
    eventSearch,
    userId,
    currentPage,
    locale,
    sessionId,
    guestId,
}: {
    id: string;
    action: string;
    events: Events;
    searchId: string;
    eventSearch: any;
    userId: string;
    currentPage: number;
    locale?: string;
    sessionId: string;
    guestId: string;
}) {
    return {
        user: {
            id: userId,
            locale,
            guestId,
            sessionId,
        },
        item: { id: id },
        action: action,
        place: {
            name: SEARCH_PLACEMENT_NAME,
            position: events.findIndex((event: Event) => event.id === id) + 1,
            page: currentPage + 1,
            context: {
                date_filter: eventSearch.dates || null,
                query_terms: eventSearch.q || null,
                is_online: eventSearch.onlineEventsOnly,
                place_id: Array.isArray(eventSearch.places)
                    ? eventSearch.places[0]
                    : null,
                latitude: null,
                longitude: null,
                search_id: searchId,
            },
            tabKey: '',
            subInterface: {
                name: '',
                position: '',
            },
        },
    };
}

const areEqual = (
    prevProps: ISearchPrimaryResultsEventCardWrapper,
    nextProps: ISearchPrimaryResultsEventCardWrapper,
) => {
    //return true if no re-render
    //return false if should re-render
    if (prevProps.isRaised && !nextProps.isRaised) {
        return false;
    }

    if (!prevProps.isRaised && nextProps.isRaised) {
        return false;
    }

    if (nextProps.scrollId === nextProps.id && !prevProps.scrollId) {
        return false;
    }

    //Handle clientside expansion/hydration to re-render the card
    if (prevProps.organizerName !== nextProps.organizerName) {
        return false;
    }

    return true;
};

interface ISearchPrimaryResultsEventCardWrapper extends SearchEventCardProps {
    id: string;
    results: undefined;
    isRaised: boolean;
    scrollId?: string;
    setHoverEventTarget: Function;
}

const SearchPrimaryResultsEventCardWrapper: React.FunctionComponent<
    ISearchPrimaryResultsEventCardWrapper
> = (props) => {
    const cardRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (props.scrollId === props.id && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [props.scrollId, props.id]);

    return (
        <div
            className="search-event-card-wrapper"
            onMouseEnter={() => props.setHoverEventTarget(props.id)}
            onFocus={() => props.setHoverEventTarget(props.id)}
            onMouseLeave={() => props.setHoverEventTarget(null)}
            onBlur={() => props.setHoverEventTarget(null)}
            role="presentation"
            ref={cardRef}
        >
            <SearchEventCardStyleSwitcher {...props} />
        </div>
    );
};

const MemoizedSearchPrimaryResultsEventCardWrapper = React.memo(
    SearchPrimaryResultsEventCardWrapper,
    areEqual,
);

const useTrackDedupeEvents = (content: $FixMe) => {
    useMount(() => {
        const checkForDedup = ({
            dedup,
        }: {
            dedup?: { count: number; hash?: string };
        }) => dedup && typeof dedup.count === 'number' && dedup.count > 1;
        const hasDedupedEvent = content?.some((event: $FixMe) =>
            checkForDedup(event),
        );

        if (hasDedupedEvent) {
            trackAnalyticsEvent({
                category: 'search',
                action: 'DeDupedResultsShown',
            });
        }
    });
};

interface SearchResultsProps {
    results?: Events;
    history?: $FixMe;
    onMouseOverCard?: Function;
    onMouseOutCard?: MouseEventHandler<HTMLDivElement> &
        FocusEventHandler<HTMLDivElement>;
    trackEventClick?: Function;
    trackEventClickInternal?: Function;
    trackGAEvent?: Function;
    isAuthenticated?: boolean;
    isOnline?: boolean;
    isMobile?: boolean;
    featureFlags?: FeatureFlags;
    shouldShowDebugOutput?: boolean;
    setHoverEventTarget: Function;
    isLoading: boolean;
    hasEventResults: boolean;
    userId: string;
    searchId: string;
    eventSearch: $FixMe;
    focusedEvents: string[];
    raisedEvents: string[];
    currentPage: number;
    sessionId: string;
    guestId: string;
}

export const SearchResultsEvents = (props: SearchResultsProps) => {
    const placementDomElement = useRef(null);
    const { locale } = useContext(TrackingContext);

    useTrackDedupeEvents(props.results);
    usePlacementEventContextAggregator(placementDomElement, ({ id, action }) =>
        aggregateContext({
            id,
            action,
            events: props?.results || [],
            eventSearch: props.eventSearch,
            searchId: props.searchId,
            userId: props.userId,
            currentPage: props.currentPage,
            locale,
            sessionId: props.sessionId,
            guestId: props.guestId,
        }),
    );

    if (!props.isLoading && !props.hasEventResults) {
        return null;
    }

    return (
        <section ref={placementDomElement}>
            {props.isLoading ? (
                <LoadingCards style="list-responsive" isFlipped={true} />
            ) : (
                <ul className="search-main-content__events-list">
                    {props.results?.map(
                        ({ id, ...cardProps }: any, index: number) => (
                            <li key={id}>
                                <MemoizedSearchPrimaryResultsEventCardWrapper
                                    trackClick={props.trackEventClick?.bind(
                                        null,
                                        index,
                                        id,
                                    )}
                                    type={LIST_TYPE}
                                    isAuthenticated={props.isAuthenticated}
                                    containerSpacingClasses={
                                        props.isMobile ? '' : 'eds-l-pad-all-4'
                                    }
                                    trackClickInternal={props.trackEventClickInternal?.bind(
                                        null,
                                        index,
                                    )}
                                    trackGAEvent={props.trackGAEvent}
                                    section={SEARCH_SECTION}
                                    featureFlags={props.featureFlags}
                                    position={index + 1}
                                    shouldShowDebugOutput={
                                        props.shouldShowDebugOutput
                                    }
                                    history={props.history}
                                    setHoverEventTarget={
                                        props.setHoverEventTarget
                                    }
                                    isLazyImage={index > 3}
                                    heapLocationString="Search - Primary Results"
                                    isRaised={
                                        props.focusedEvents.includes(id) ||
                                        props.raisedEvents.includes(id)
                                    }
                                    scrollId={props.focusedEvents[0]}
                                    id={id}
                                    {...cardProps}
                                />
                            </li>
                        ),
                    )}
                </ul>
            )}
        </section>
    );
};

export const _mapStateToProps = (state: any, ownProps: any) => {
    const section = getSearchSection(state.location.isOnline);
    const results = getSearchContent(
        state.content.entities,
        state.pagination,
        state.currencyFormat,
        section,
    );

    return {
        results: getEventResults(results),
        history: ownProps.history,
        hasEventResults: hasEventResults(results),
        eventSearch: state.search.eventSearch,
        isAuthenticated: state.auth.isAuthenticated,
        isLoading: state.pagination.loading,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        userId: state.app.publicId,
        searchId: state.search.searchId,
        focusedEvents: state.ui.focusedEventCards,
        raisedEvents: state.ui.raisedEventCards,
        currentPage: state.pagination.currentPage,
        sessionId: state.app.sessionId,
        guestId: state.app.guestId,
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    setHoverEventTarget: (...args: [$FixMe]) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
    getEventExpansions: () => dispatch(getEventExpansionsAction()),
});

export const PrimarySearchResultsEventsContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(SearchResultsEvents);
